<template>
  <v-card class="mb-4" variant="outlined">
    <v-card-title class="text-primary weight-600 d-flex align-center">
      <v-icon icon="mdi-auto-fix" class="mr-2"></v-icon>
      Autofill from Documents
    </v-card-title>
    <v-card-text>
      <div class="text-body-2 text-medium-emphasis mb-2">
        Add screenshots or PDFs of anything that helps build the prior authorization, such as:
        <ul class="autofill-examples ml-6 my-1">
          <li>Patient demographics</li>
          <li>Prescriber information</li>
          <li>Insurance details (BIN/PCN)</li>
          <li>Patient diagnosis</li>
          <li>Prescription details, including the sig</li>
        </ul>
      </div>

      <slot />
      <v-alert v-if="documentsOverCap > 0" type="warning" variant="tonal" density="compact" class="mt-2">
        This submission has {{ props.supportingDocsCount }} documents, and autofill reads at most
        {{ AUTOFILL_MAX_DOCUMENTS_PER_RUN }} of them. {{ documentsOverCap }} will not be read. Remove the ones autofill
        does not need.
      </v-alert>

      <v-divider class="my-4" />

      <div class="d-flex align-center flex-wrap ga-4">
        <v-btn
          color="primary"
          prepend-icon="mdi-auto-fix"
          :disabled="props.supportingDocsCount === 0 || props.isRunning || props.documentsBusy"
          :loading="props.isRunning"
          @click="emit('run')">
          Autofill from Documents
        </v-btn>
        <span v-if="props.documentsBusy && !props.isRunning" class="text-body-2 text-medium-emphasis">
          Wait for the uploads to finish.
        </span>
        <v-btn
          v-if="runStarted"
          variant="outlined"
          color="primary"
          prepend-icon="mdi-open-in-new"
          @click="emit('startAnother')">
          Start Another Submission
          <v-tooltip activator="parent" location="top" max-width="320">
            Opens a blank submission in a new tab. This draft and its autofill stay in this tab, so come back here to
            review and create it.
          </v-tooltip>
        </v-btn>
        <span v-if="props.status === AUTOFILL_STATUS.QUEUED" class="text-body-2 text-medium-emphasis">
          Queued — waiting for a worker to pick this up.
        </span>
        <span v-else-if="props.status === AUTOFILL_STATUS.PROCESSING" class="text-body-2 text-medium-emphasis">
          Reading the documents. This usually takes under a minute.
        </span>
      </div>

      <v-alert v-if="props.error" type="warning" variant="tonal" density="compact" class="mt-4">
        {{ props.error }}
      </v-alert>
      <v-alert
        v-else-if="props.status === AUTOFILL_STATUS.DONE && props.candidateCount > 0"
        type="success"
        variant="tonal"
        density="compact"
        class="mt-4">
        Found values for {{ props.candidateCount }} {{ props.candidateCount === 1 ? "field" : "fields" }} and filled the
        empty ones in — each applied value is marked green under its field. Click another value to swap it in.
      </v-alert>

      <template v-if="props.candidates && props.candidateCount > 0">
        <v-divider class="my-4" />
        <AutofillReviewPanel
          :candidates="props.candidates"
          :diagnoses="props.diagnoses"
          :medication-matches="props.medicationMatches" />
      </template>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { computed } from "vue";
import AutofillReviewPanel from "@/components/AutofillReviewPanel.vue";
import { AUTOFILL_MAX_DOCUMENTS_PER_RUN, AUTOFILL_STATUS } from "@/constants/autofill";

const props = defineProps({
  // Number of documents on the submission: sent to the payer plus AI only.
  supportingDocsCount: {
    type: Number,
    default() {
      return 0;
    },
  },
  isRunning: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // A document is still uploading or preparing, so a run now would not read it.
  documentsBusy: {
    type: Boolean,
    default: false,
  },
  // One of AUTOFILL_STATUS — mirrors the poll endpoint's status.
  status: {
    type: String,
    default() {
      return AUTOFILL_STATUS.IDLE;
    },
  },
  error: {
    type: String,
    default() {
      return "";
    },
  },
  candidateCount: {
    type: Number,
    default() {
      return 0;
    },
  },
  // Per-field candidates from the last run, keyed by orderInfo field name.
  candidates: {
    type: Object,
    default() {
      return null;
    },
  },
  // Diagnosis candidates, kept separate because they are (name, code) pairs and the order
  // takes one primary plus any number of secondaries.
  diagnoses: {
    type: Array,
    default() {
      return [];
    },
  },
  // Catalog rows matched by the run (MedicationDetail instances); only their count matters here.
  medicationMatches: {
    type: Array,
    default() {
      return [];
    },
  },
});

const emit = defineEmits(["run", "startAnother"]);

const runStarted = computed(() =>
  [AUTOFILL_STATUS.QUEUED, AUTOFILL_STATUS.PROCESSING, AUTOFILL_STATUS.DONE].includes(props.status)
);

// The run reads the submission's payer and AI-only documents together, capped per run.
const documentsOverCap = computed(() => Math.max(0, props.supportingDocsCount - AUTOFILL_MAX_DOCUMENTS_PER_RUN));
</script>

<style lang="scss" scoped>
.autofill-examples li {
  list-style: disc;
}

@import "../styles/main.scss";
</style>
